import { classNames } from '@fullstory/util/fmt';
import React, { CSSProperties, forwardRef, HTMLAttributes } from 'react';
import { FSMaskProp } from '../../types';
import { identifyComponent } from '../../util';
import css, { ClassNames } from './style.module.scss';

export type JustifyContent =
  | 'center'
  | 'flex-start'
  | 'flex-end'
  | 'space-between'
  | 'space-evenly'
  | 'space-around'
  | 'stretch';
type AlignItems = 'center' | 'flex-start' | 'flex-end' | 'stretch' | 'baseline' | 'normal';
type FlexWrap = 'wrap' | 'nowrap' | 'wrap-reverse';

const justifyContentMap: { [k in JustifyContent]: ClassNames } = {
  center: 'justify-center',
  'flex-start': 'justify-flex-start',
  'flex-end': 'justify-flex-end',
  'space-between': 'justify-space-between',
  'space-evenly': 'justify-space-evenly',
  'space-around': 'justify-space-around',
  stretch: 'justify-stretch',
};

const alignItemsMap: { [k in AlignItems]: ClassNames } = {
  center: 'align-center',
  'flex-start': 'align-flex-start',
  'flex-end': 'align-flex-end',
  stretch: 'align-stretch',
  baseline: 'align-baseline',
  normal: 'align-normal',
};

const flexWrapMap: { [k in FlexWrap]: ClassNames } = {
  wrap: 'wrap-wrap',
  nowrap: 'wrap-nowrap',
  'wrap-reverse': 'wrap-wrap-reverse',
};

export interface FlexProps extends HTMLAttributes<HTMLDivElement> {
  testId?: string;
  inline?: boolean;
  column?: boolean;
  reverse?: boolean;
  justifyContent?: JustifyContent;
  alignItems?: AlignItems;
  flexWrap?: FlexWrap;
  gap?: CSSProperties['gap'];
  className?: string;
  fsMask?: FSMaskProp;
}

// eslint-disable-next-line react/display-name
export const Flex = forwardRef<HTMLDivElement, FlexProps>(
  (
    {
      testId = 'flex',
      inline = false,
      column = false,
      justifyContent = 'center',
      alignItems = 'center',
      reverse = false,
      className,
      flexWrap,
      gap,
      fsMask = null,
      style,
      ...restOfProps
    },
    ref,
  ) => (
    <div
      data-component="Flex"
      // If data-testid is passed as a prop, overrides testId
      data-testid={testId}
      {...restOfProps}
      {...identifyComponent('flex', restOfProps)}
      ref={ref}
      style={{
        gap,
        ...style,
      }}
      className={classNames(
        className,
        fsMask,
        css.flexComponent,
        css[column ? 'column' : 'row'],
        css[justifyContentMap[justifyContent]],
        css[alignItemsMap[alignItems]],
        flexWrap ? css[flexWrapMap[flexWrap]] : null,
        {
          [css.inline]: inline,
          [css.reverse]: reverse,
        },
      )}
    />
  ),
);
Flex.displayName = 'Flex';
